import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useGetModuleCollectionsQuery } from "@redux/features/collections/collectionsApi";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseInput from "@reusables/BaseInput";
import BaseButton from "@reusables/BaseButton";
import { Collapse } from "@mui/material";
import CollectionSelector from "../components/CollectionSelector";
import TableButton from "@reusables/BaseTable/TableButton";
import { cn } from "@helpers/utils";
import { supplierSchema } from "../../../types";

const formScheme = z.object({
    collection: z.object({
        id: z.number(),
        code: z.string(),
        name: z.string(),
        barcode: z.string().nullish(),
        totalQuantity: z.number(),
        products: z.array(
            z.object({
                id: z.number(),
                name: z.string(),
                code: z.string(),
                in_stock: z.number(),
                prices: z.object({
                    purchase_price: z.number()
                }),
                min_purchase_quantity: z.number(),
                min_sale_quantity: z.number(),
                supplier: supplierSchema.nullish(),

                tax: z.object({
                    id: z.number(),
                    rate: z.number()
                }).nullish(),

                quantity: z.number()
            })
        )
    }),
    quantity: z.coerce.number().min(0)
});

export type FormTyping = z.infer<typeof formScheme>;

export interface AddCollectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLineAddition: (chosenCollection: FormTyping) => void;
}

export default function AddCollectionModal({
                                               isOpen,
                                               onLineAddition,
                                               onClose
                                           }: AddCollectionModalProps): JSX.Element {
    const { t } = useTranslation("", {
        keyPrefix: "purchaseOrder.general.orders.modals.addCollection"
    });

    const { data: collectionOptions, isLoading: collectionOptionsLoading } =
        useGetModuleCollectionsQuery("purchase-order");

    const { control, ...form } = useForm<FormTyping>({
        resolver: zodResolver(formScheme)
    });

    // State to track which collection is being viewed
    const [viewCollection, setViewCollection] = useState<number | null>(null);

    const onSubmit = form.handleSubmit((data) => {
        onLineAddition(data);
        form.reset();
        onClose();
    }, console.error);

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                isLoading={collectionOptionsLoading}
                width={900}
                padding="56px"
            >
                <form className="space-y-[32px]" onSubmit={onSubmit}>
                    <div className="modal-title">{t("heading")}</div>
                    <BaseInputsGrid cols={1}>
                        <Controller
                            name="collection"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    {...fieldState}
                                    label={t("fields.collection.label") + " *"}
                                    isLoading={collectionOptionsLoading}
                                    placeholder={t("fields.collection.placeholder")}
                                    options={collectionOptions}
                                    onChange={(e) => {
                                        setViewCollection(null);
                                        field.onChange(e);
                                    }}
                                    getter={{
                                        label: (opt) => opt?.name,
                                        key: (opt) => opt?.id,
                                        renderOption: (opt, icon) => (
                                            <div className="flex flex-col">
                                                <div className="flex w-full justify-between items-center">
                                                    <div>{opt.name}</div>
                                                    <div className={"flex flex-row items-center space-x-2"}>
                                                        {
                                                            opt.id === field.value?.id &&
                                                            <div>
                                                                {icon}
                                                            </div>
                                                        }
                                                        <TableButton
                                                            onClick={(e: any) => {
                                                                // Prevent dropdown selection
                                                                e.stopPropagation();
                                                                // Toggle the collection view
                                                                setViewCollection((prev) =>
                                                                    prev === opt.id ? null : opt.id
                                                                );
                                                            }}
                                                            className={cn(
                                                                "border-[1px] border-solid transition-all",
                                                                viewCollection === opt.id && "bg-white !border-purple-400"
                                                            )}
                                                        >
                                                            {viewCollection === opt.id
                                                                ? t("selector.buttons.hideDetails")
                                                                : t("selector.buttons.showMore")
                                                            }
                                                        </TableButton>
                                                    </div>
                                                </div>
                                                {/* Show CollectionSelector only for the current option */}
                                                <Collapse in={viewCollection === opt.id} timeout="auto" unmountOnExit>
                                                    <CollectionSelector
                                                        products={opt.products}
                                                    />
                                                </Collapse>
                                            </div>
                                        )
                                    }}
                                />
                            )}
                        />

                        <Controller
                            name="quantity"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.quantity.label")}
                                    placeholder={t("fields.quantity.placeholder")}
                                />
                            )}
                        />
                    </BaseInputsGrid>
                    <BaseButton text={t("buttons.add")} size="md" className="w-full" />
                </form>
            </BaseModal>
        </>
    );
}
